import { useCallback, useEffect, useState } from 'react'
import { CheckCircle2, ChevronRight, Loader2, LogOut, Vote } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  authStart, authVerify, castVote, logout, memberToken, myBallot, myContests,
  MemberApiError, type BallotEntry, type MemberContest,
} from './api'

/**
 * The member voting app — a different application that happens to share a bundle.
 *
 * ## Assume a cheap Android phone on 3G
 *
 * No sidebar, no shell, no election selector, no analytics, no web fonts. Big tap targets,
 * one decision per screen, and every string short enough to read on a 5-inch display in
 * sunlight. A member opens this once every five years and must not need to be taught it.
 *
 * ## Several ballots at once is the NORMAL case
 *
 * A member can have presidential, governor, senator, woman-rep, MP and MCA primaries open on
 * the same day. The list makes outstanding ones obvious and keeps the finished ones visible
 * so nobody wonders whether their vote registered.
 *
 * ## The receipt is shown once
 *
 * Only its hash is stored, so it cannot be recovered. It proves the ballot was counted; it
 * cannot reveal how it was cast — which is precisely what lets a member check their own vote
 * without anyone being able to check it for them.
 */
type Screen =
  | { name: 'id' }
  | { name: 'code'; nationalId: string }
  | { name: 'ballots' }
  | { name: 'ballot'; contest: MemberContest }
  | { name: 'done'; contest: MemberContest; receipt: string }

export function VoteApp() {
  const [screen, setScreen] = useState<Screen>(
    memberToken.get() ? { name: 'ballots' } : { name: 'id' })
  const [error, setError] = useState<string | null>(null)

  const signedOut = useCallback((message?: string) => {
    memberToken.clear()
    setScreen({ name: 'id' })
    setError(message ?? null)
  }, [])

  return (
    <div className="min-h-dvh bg-background text-foreground flex flex-col">
      <header className="border-b border-border px-4 py-3 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 font-semibold text-erp-base">
          <Vote className="h-4 w-4 text-chocolate" /> Nomination voting
        </span>
        {screen.name !== 'id' && screen.name !== 'code' && (
          <button type="button"
            onClick={() => void logout().finally(() => signedOut())}
            className="inline-flex items-center gap-1.5 text-erp-xs text-muted-foreground
                       hover:text-foreground">
            <LogOut className="h-3.5 w-3.5" /> Finish
          </button>
        )}
      </header>

      <main className="flex-1 w-full max-w-md mx-auto px-4 py-6">
        {error && (
          <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-erp-sm
                        text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </p>
        )}

        {screen.name === 'id' && (
          <IdScreen onSent={(nationalId) => {
            setError(null)
            setScreen({ name: 'code', nationalId })
          }} onError={setError} />
        )}

        {screen.name === 'code' && (
          <CodeScreen nationalId={screen.nationalId}
            onIn={() => { setError(null); setScreen({ name: 'ballots' }) }}
            onError={setError}
            onBack={() => { setError(null); setScreen({ name: 'id' }) }} />
        )}

        {screen.name === 'ballots' && (
          <BallotsScreen
            onPick={(contest) => { setError(null); setScreen({ name: 'ballot', contest }) }}
            onExpired={signedOut} onError={setError} />
        )}

        {screen.name === 'ballot' && (
          <BallotScreen contest={screen.contest}
            onCast={(receipt) => {
              setError(null)
              setScreen({ name: 'done', contest: screen.contest, receipt })
            }}
            onBack={() => { setError(null); setScreen({ name: 'ballots' }) }}
            onExpired={signedOut} onError={setError} />
        )}

        {screen.name === 'done' && (
          <DoneScreen contest={screen.contest} receipt={screen.receipt}
            onMore={() => { setError(null); setScreen({ name: 'ballots' }) }} />
        )}
      </main>
    </div>
  )
}

// ── 1. who are you ───────────────────────────────────────────────────────────────────────

function IdScreen({ onSent, onError }: {
  onSent: (nationalId: string) => void
  onError: (message: string | null) => void
}) {
  const [nationalId, setNationalId] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true)
    try {
      onError(null)
      /*
         The response's maskedHint is deliberately IGNORED. It is derived from the ID that
         was typed, not from the register — it has to be, or the response would differ for a
         registered and an unregistered number and turn this form into an enumeration oracle.
         Showing it would put a plausible-but-wrong phone number in front of a real member,
         who would reasonably conclude they had mistyped their own ID.
      */
      await authStart(nationalId.trim())
      onSent(nationalId.trim())
    } catch (err) {
      onError(err instanceof MemberApiError ? err.message : 'Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void submit() }}>
      <div>
        <h1 className="text-erp-lg font-semibold">Enter your ID number</h1>
        <p className="text-erp-sm text-muted-foreground mt-1">
          We will send a code to the phone number on your registration.
        </p>
      </div>
      <input value={nationalId} onChange={(e) => setNationalId(e.target.value)}
        inputMode="numeric" autoComplete="off" maxLength={64} autoFocus
        className="erp-input h-14 w-full text-erp-lg tracking-wide text-center"
        placeholder="ID number" aria-label="ID number" />
      <button type="submit" disabled={busy || nationalId.trim().length < 4}
        className={cn('erp-btn-primary h-14 w-full text-erp-base inline-flex items-center',
          'justify-center gap-2',
          (busy || nationalId.trim().length < 4) && 'opacity-50')}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send my code
      </button>
    </form>
  )
}

// ── 2. the code ──────────────────────────────────────────────────────────────────────────

function CodeScreen({ nationalId, onIn, onError, onBack }: {
  nationalId: string
  onIn: () => void; onError: (m: string | null) => void; onBack: () => void
}) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true)
    try {
      onError(null)
      await authVerify(nationalId, code.trim())
      onIn()
    } catch (err) {
      onError(err instanceof MemberApiError ? err.message : 'Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void submit() }}>
      <div>
        <h1 className="text-erp-lg font-semibold">Enter the code</h1>
        {/*
            The hint is generated from the ID you typed, NOT from the register — it has to be
            identical for a registered and an unregistered number, or this screen would tell
            anyone whether any ID is on the roll. So it confirms a code was sent, not where.
        */}
        <p className="text-erp-sm text-muted-foreground mt-1">
          If that ID is registered, a 6-digit code has been sent to the phone on record.
          It expires in 5 minutes.
        </p>
      </div>
      <input value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
        inputMode="numeric" maxLength={6} autoFocus autoComplete="one-time-code"
        className="erp-input h-14 w-full text-center text-erp-lg tracking-[0.4em]"
        placeholder="000000" aria-label="6-digit code" />
      <button type="submit" disabled={busy || code.length !== 6}
        className={cn('erp-btn-primary h-14 w-full text-erp-base inline-flex items-center',
          'justify-center gap-2', (busy || code.length !== 6) && 'opacity-50')}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Continue
      </button>
      <button type="button" onClick={onBack}
        className="w-full text-erp-sm text-muted-foreground hover:text-foreground">
        Use a different ID number
      </button>
    </form>
  )
}

// ── 3. which ballot ──────────────────────────────────────────────────────────────────────

function BallotsScreen({ onPick, onExpired, onError }: {
  onPick: (c: MemberContest) => void
  onExpired: (m?: string) => void
  onError: (m: string | null) => void
}) {
  const [contests, setContests] = useState<MemberContest[] | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        setContests(await myContests())
      } catch (err) {
        if (err instanceof MemberApiError && err.status === 401) onExpired(err.message)
        else onError(err instanceof MemberApiError ? err.message : 'Please try again.')
        setContests([])
      }
    })()
  }, [onExpired, onError])

  if (contests === null) {
    return <p className="text-center text-erp-sm text-muted-foreground py-8">
      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading your ballots
    </p>
  }

  const outstanding = contests.filter((c) => c.outstanding)
  const done = contests.filter((c) => !c.outstanding)

  return (
    <div className="space-y-5">
      <h1 className="text-erp-lg font-semibold">Your ballots</h1>

      {outstanding.length === 0 && done.length === 0 && (
        <p className="text-erp-sm text-muted-foreground">
          You have no nominations open for voting right now.
        </p>
      )}

      {outstanding.length > 0 && (
        <div className="space-y-2">
          <p className="text-erp-sm text-muted-foreground">
            {outstanding.length === 1 ? 'One ballot is waiting for you.'
              : `${outstanding.length} ballots are waiting for you.`}
          </p>
          {outstanding.map((c) => (
            <button key={c.id} type="button" onClick={() => onPick(c)}
              className="w-full rounded-lg border border-border px-4 py-4 text-left
                         hover:border-chocolate hover:bg-chocolate-soft/20 flex items-center
                         justify-between gap-3">
              <span>
                <span className="block font-medium">{c.title}</span>
                {c.delegate && (
                  <span className="block text-erp-xs text-muted-foreground">
                    You are voting as a delegate
                  </span>
                )}
              </span>
              <ChevronRight className="h-5 w-5 text-chocolate shrink-0" />
            </button>
          ))}
        </div>
      )}

      {done.length > 0 && (
        <div className="space-y-2">
          <p className="text-erp-xs uppercase tracking-wide text-muted-foreground">Done</p>
          {done.map((c) => (
            <div key={c.id}
              className="rounded-lg border border-border px-4 py-3 flex items-center gap-2
                         text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
              <span className="text-erp-sm">{c.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── 4. the ballot ────────────────────────────────────────────────────────────────────────

function BallotScreen({ contest, onCast, onBack, onExpired, onError }: {
  contest: MemberContest
  onCast: (receipt: string) => void
  onBack: () => void
  onExpired: (m?: string) => void
  onError: (m: string | null) => void
}) {
  const [ballot, setBallot] = useState<BallotEntry[] | null>(null)
  const [chosen, setChosen] = useState<BallotEntry | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void (async () => {
      try {
        setBallot(await myBallot(contest.id))
      } catch (err) {
        if (err instanceof MemberApiError && err.status === 401) onExpired(err.message)
        else onError(err instanceof MemberApiError ? err.message : 'Please try again.')
        setBallot([])
      }
    })()
  }, [contest.id, onExpired, onError])

  const confirm = async () => {
    if (!chosen) return
    setBusy(true)
    try {
      onError(null)
      const receipt = await castVote(contest.id, chosen.aspirantId)
      onCast(receipt.receipt)
    } catch (err) {
      if (err instanceof MemberApiError && err.status === 401) onExpired(err.message)
      else onError(err instanceof MemberApiError ? err.message : 'Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (ballot === null) {
    return <p className="text-center text-erp-sm text-muted-foreground py-8">
      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
    </p>
  }

  // One decision per screen: pick, THEN confirm. A single-tap cast on a phone in a crowd is
  // a mis-tap somebody cannot undo — a ballot is never editable once it is in.
  if (chosen) {
    return (
      <div className="space-y-5">
        <h1 className="text-erp-lg font-semibold">Confirm your vote</h1>
        <div className="rounded-lg border-2 border-chocolate px-4 py-5 text-center">
          <span className="block text-erp-xs text-muted-foreground">{contest.title}</span>
          <span className="block text-erp-lg font-semibold mt-1">{chosen.ballotName}</span>
        </div>
        <p className="text-erp-sm text-muted-foreground">
          Once cast, a vote cannot be changed or withdrawn.
        </p>
        <button type="button" disabled={busy} onClick={() => void confirm()}
          className={cn('erp-btn-primary h-14 w-full text-erp-base inline-flex items-center',
            'justify-center gap-2', busy && 'opacity-50')}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Yes, cast my vote
        </button>
        <button type="button" onClick={() => setChosen(null)}
          className="w-full text-erp-sm text-muted-foreground hover:text-foreground">
          Choose someone else
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <button type="button" onClick={onBack}
          className="text-erp-sm text-muted-foreground hover:text-foreground mb-2">
          ← All ballots
        </button>
        <h1 className="text-erp-lg font-semibold">{contest.title}</h1>
        <p className="text-erp-sm text-muted-foreground mt-1">Choose one.</p>
      </div>

      {ballot.length === 0 && (
        <p className="text-erp-sm text-muted-foreground">
          No candidates have been cleared for this contest yet.
        </p>
      )}

      <div className="space-y-2">
        {ballot.map((a) => (
          <button key={a.aspirantId} type="button" onClick={() => setChosen(a)}
            className="w-full rounded-lg border border-border px-4 py-4 text-left
                       hover:border-chocolate hover:bg-chocolate-soft/20 flex items-center
                       gap-3">
            <span className="w-7 h-7 rounded-full bg-muted text-muted-foreground
                             inline-flex items-center justify-center text-erp-sm shrink-0">
              {a.ballotPosition ?? '•'}
            </span>
            <span className="font-medium">{a.ballotName}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

// ── 5. the receipt ───────────────────────────────────────────────────────────────────────

function DoneScreen({ contest, receipt, onMore }: {
  contest: MemberContest; receipt: string; onMore: () => void
}) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="space-y-5 text-center">
      <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
      <div>
        <h1 className="text-erp-lg font-semibold">Your vote is in</h1>
        <p className="text-erp-sm text-muted-foreground mt-1">{contest.title}</p>
      </div>

      <div className="rounded-lg border border-border px-4 py-4">
        <span className="block text-erp-xs text-muted-foreground mb-1">Your receipt</span>
        <span className="block font-mono text-erp-base tracking-wider break-all">{receipt}</span>
        <button type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(receipt).then(() => setCopied(true))
          }}
          className="mt-2 text-erp-sm text-chocolate hover:underline">
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {/*
          Says exactly what the receipt is FOR, and what it is not. A member who thinks it
          proves who they voted for may show it to somebody who is buying votes.
      */}
      <p className="text-erp-sm text-muted-foreground">
        Keep this to confirm your vote was counted. It does <strong>not</strong> show who you
        voted for — nobody, including us, can work that out from it.
      </p>

      <button type="button" onClick={onMore}
        className="erp-btn h-12 w-full text-erp-base">
        Back to my ballots
      </button>
    </div>
  )
}
